import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, Flex, Typography } from 'antd';
import { useState } from 'react';

import Button from 'components/Button';
import Show from 'components/Show';
import TaskInfo from 'components/TaskInfo';

import useMediaQuery from 'hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { TaskDetail } from 'screens/task-management/types';
import { renderDate } from 'utils/helpers';
import CheckpointProgressBar from 'components/CheckpointProgressBar';
import DateTime from 'utils/datetime';
import { calculateDatePercentage } from 'screens/task-management/util';

const Indicator = ({
  color,
  children,
}: {
  color: string;
  children: React.ReactNode;
}) => (
  <Flex align="center" gap={8}>
    <span
      className="dot"
      style={{
        backgroundColor: color,
      }}
    ></span>
    <span style={{ color }}>{children}</span>
  </Flex>
);

type TimeWindowCardProps = Pick<
  TaskDetail,
  'plannedDate' | 'startDate' | 'endDate' | 'status'
>;

const calculateCheckpointDate = (
  startDate: string,
  endDate: string,
  plannedDate: string,
) => {
  const totalDays = DateTime.diff(endDate, startDate, 'days') || 0;
  const elapsedDays = DateTime.diff(plannedDate, startDate, 'days') || 0;

  const percentage = Math.round((elapsedDays / totalDays) * 100);

  return Math.min(Math.max(percentage, 0), 100);
};

const getProgressBarColor = (status?: string) => {
  if (status === 'INCOMPLETE') return '#FF4D4F';
  if (status === 'COMPLETE') return '#4096FF';
  if (status === 'UPCOMING') return '#00000073';
  return undefined;
};

const TimeWindowCard = ({
  plannedDate,
  startDate,
  endDate,
  status,
}: TimeWindowCardProps) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const isMobile = useMediaQuery('(max-width: 768px)');

  const progress = calculateDatePercentage(startDate, endDate);
  const checkpoint = calculateCheckpointDate(startDate, endDate, plannedDate);
  const checkpointColor = getProgressBarColor(status);

  return (
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between">
        <Typography.Text strong>
          {t('taskManagement.labels.timeWindow')}
        </Typography.Text>
        <Button
          trackerLabel={`Time Window Info ${open ? 'Hide' : 'Show'}`}
          type="link"
          onClick={() => setOpen(!open)}
          className="d-flex align-items-center px-0"
        >
          <InfoCircleOutlined
            style={{ color: 'blue', width: 15, height: 15 }}
          />
          <span>{open ? t('common.hide') : t('common.show')}</span>
        </Button>
      </Flex>

      <Show when={open}>
        <Card size="small" style={{ background: 'rgba(0, 0, 0, 0.02)' }}>
          <Flex vertical gap={12}>
            <Flex
              vertical={isMobile}
              wrap="wrap"
              className="row-wrap-container"
              justify="space-between"
            >
              <TaskInfo
                header={t('taskManagement.labels.timeWindow')}
                renderValue={() => (
                  <Flex gap={4} align="center">
                    <span>{renderDate(startDate)}</span>
                    {' - '}
                    <span>{renderDate(endDate)}</span>
                  </Flex>
                )}
              />
              <TaskInfo
                wrapperStyle={{ flex: 'none' }}
                header={t('taskManagement.labels.plannedDate')}
                value={renderDate(plannedDate)}
                wrapperClass={`${isMobile ? '' : 'text-end'}`}
              />
            </Flex>

            <Show when={status === 'INCOMPLETE'}>
              <Indicator color="#FF4D4F">
                {t('taskManagement.labels.pastTimeWindow')}
              </Indicator>
            </Show>
            <Show when={status === 'LATE'}>
              <Indicator color="#FAAD14">
                {t('taskManagement.labels.pastPlannedDate')}
              </Indicator>
            </Show>
          </Flex>
        </Card>
      </Show>

      <CheckpointProgressBar
        checkpoint={checkpoint}
        percent={progress}
        checkpointColor={getProgressBarColor(status)}
        strokeColor={checkpointColor}
        checkpointLabel={renderDate(plannedDate)}
        startLabel={renderDate(startDate)}
        endLabel={renderDate(endDate)}
      />
    </Flex>
  );
};

export default TimeWindowCard;
